import { useEffect, useState } from "react";
import type { Facets } from "./FacetRail";

export interface SavedSearch {
  id: string;
  query: string;
  facets: Facets;
  created_at: string;
}

const STORAGE_KEY = "docustudio-saved-searches";

function readAll(): SavedSearch[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedSearch[]) : [];
  } catch {
    return [];
  }
}

function writeAll(items: SavedSearch[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event("docustudio-saved-searches"));
  } catch {
    // ignore
  }
}

export function addSavedSearch(body: Omit<SavedSearch, "id" | "created_at">): SavedSearch {
  const item: SavedSearch = {
    id: `ss_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    created_at: new Date().toISOString(),
    ...body,
  };
  const items = readAll();
  // Deduplicate by query string (overwrite if same)
  const existing = items.findIndex((i) => i.query.toLowerCase() === body.query.toLowerCase());
  if (existing >= 0) items.splice(existing, 1);
  items.unshift(item);
  writeAll(items.slice(0, 20));
  return item;
}

export function removeSavedSearch(id: string): void {
  writeAll(readAll().filter((i) => i.id !== id));
}

/**
 * B4.4 — Saved searches via localStorage (no backend yet).
 *
 * When the backend endpoint /v2/search/saved lands we can swap this for a
 * react-query backed list while keeping the same hook shape.
 */
export function useSavedSearches(): { items: SavedSearch[] } {
  const [items, setItems] = useState<SavedSearch[]>(() => readAll());
  useEffect(() => {
    function refresh() {
      setItems(readAll());
    }
    window.addEventListener("docustudio-saved-searches", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("docustudio-saved-searches", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  return { items };
}
